import { useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn, timeAgo } from "../lib/utils";
import type { Card } from "../lib/api";

interface KanbanCardContentProps {
  card: Card;
  onClick?: () => void;
  isDragging?: boolean;
  isDragOverlay?: boolean;
  isLocked?: boolean;
  isBlocked?: boolean;
  blockerCount?: number;
}

/**
 * Pure presentational card component — no dnd-kit hooks.
 * Used directly inside DragOverlay and wrapped by KanbanCard for sortable instances.
 */
export function KanbanCardContent({
  card,
  onClick,
  isDragging,
  isDragOverlay,
  isLocked,
  isBlocked,
  blockerCount,
}: KanbanCardContentProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "rounded-lg border border-gray-800 bg-surface-850 p-3 transition-all",
        isLocked
          ? "cursor-not-allowed opacity-75"
          : "cursor-pointer hover:border-gray-700 hover:bg-surface-800",
        isDragging && "opacity-40",
        isDragOverlay && "shadow-xl shadow-black/40 ring-1 ring-primary-500/30",
      )}
    >
      {/* Title */}
      <div className="flex items-start gap-2">
        <p
          className={cn(
            "text-sm leading-snug",
            card.title ? "font-medium text-gray-100" : "text-gray-400 italic",
          )}
        >
          {card.title ||
            card.description.slice(0, 60) +
              (card.description.length > 60 ? "…" : "") ||
            "Untitled"}
        </p>
        {card.metadata.inputRequired && (
          <span className="shrink-0 rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-400">
            {card.metadata.questions?.filter((q) => q.status === "open")
              .length || "?"}
          </span>
        )}
        {card.metadata.needsReeval && (
          <span className="shrink-0 rounded-full bg-orange-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-orange-400">
            Displaced
          </span>
        )}
        {isBlocked && (
          <span
            className="shrink-0 rounded-full bg-gray-700 px-1.5 py-0.5 text-[10px] font-semibold text-gray-300"
            title={`Blocked by ${blockerCount ?? (card.metadata.blockedBy?.length ?? 0)} unresolved prerequisite${(blockerCount ?? card.metadata.blockedBy?.length ?? 0) === 1 ? '' : 's'}`}
          >
            🔒 Blocked{blockerCount !== undefined && blockerCount > 0 ? ` (${blockerCount})` : ''}
          </span>
        )}
      </div>

      {/* Agent working indicator */}
      {isLocked && (
        <div className="flex items-center gap-1.5 mt-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500" />
          </span>
          <span className="text-[10px] font-medium text-primary-400">
            Agent working
          </span>
        </div>
      )}

      {/* Footer */}
      <div className="mt-2 flex items-center gap-2 text-[10px] text-gray-500">
        {card.metadata.prUrl && (
          <span
            className="flex items-center gap-0.5 rounded bg-purple-500/10 px-1.5 py-0.5 text-purple-400 hover:bg-purple-500/20 cursor-pointer transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              window.open(card.metadata.prUrl, "_blank", "noopener,noreferrer");
            }}
            title={card.metadata.githubPR}
          >
            <svg className="h-3 w-3" viewBox="0 0 16 16" fill="currentColor">
              <path d="M7.177 3.073L9.573.677A.25.25 0 0110 .854v4.792a.25.25 0 01-.427.177L7.177 3.427a.25.25 0 010-.354zM3.75 2.5a.75.75 0 100 1.5.75.75 0 000-1.5zm-2.25.75a2.25 2.25 0 113 2.122v5.256a2.251 2.251 0 11-1.5 0V5.372A2.25 2.25 0 011.5 3.25zM11 2.5h-1V4h1a1 1 0 011 1v5.628a2.251 2.251 0 101.5 0V5A2.5 2.5 0 0011 2.5zm1 10.25a.75.75 0 111.5 0 .75.75 0 01-1.5 0zM3.75 12a.75.75 0 100 1.5.75.75 0 000-1.5z" />
            </svg>
            {card.metadata.githubPR}
          </span>
        )}
        {card.assignedAgents.length > 0 && (
          <span className="flex items-center gap-1">
            <svg
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"
              />
            </svg>
            {card.assignedAgents.length} agent
            {card.assignedAgents.length > 1 ? "s" : ""}
          </span>
        )}
        {card.metadata.tags.length > 0 && (
          <span className="truncate">
            {card.metadata.tags.slice(0, 2).join(", ")}
          </span>
        )}
        <span className="ml-auto">{timeAgo(card.updatedAt)}</span>
      </div>
    </div>
  );
}

interface Props {
  card: Card;
  onClick: () => void;
  /** When true, disables drag listeners and touch-action override (e.g. in mobile layout) */
  disableDrag?: boolean;
  className?: string;
  /** When true, renders a "Blocked" badge and exposes blocker count */
  isBlocked?: boolean;
  blockerCount?: number;
}

export function KanbanCard({ card, onClick, disableDrag, className, isBlocked, blockerCount }: Props) {
  const isLocked = !!(
    card.metadata.claim && new Date(card.metadata.claim.expiresAt) > new Date()
  );
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: card.id,
    disabled: isLocked || !!disableDrag,
  });
  const wasDragging = useRef(false);

  // Track drag state so we can suppress click after drop
  if (isDragging) wasDragging.current = true;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(!isLocked && !disableDrag && "touch-none", className)}
      data-testid="sortable-card"
      data-card-id={card.id}
      {...attributes}
      {...(!disableDrag ? listeners : {})}
    >
      <KanbanCardContent
        card={card}
        onClick={() => {
          if (wasDragging.current) {
            wasDragging.current = false;
            return;
          }
          onClick();
        }}
        isDragging={isDragging}
        isLocked={isLocked}
        isBlocked={isBlocked}
        blockerCount={blockerCount}
      />
    </div>
  );
}
